import React, { useState } from "react";
import classes from "../TransInter.module.scss";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  rem,
  Space,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDeviceFloppy, IconTrash } from "@tabler/icons-react";
import { TransInterStepTwoProps } from "../types";
import { useTranslation } from "react-i18next";
import { useTransFormContext } from "../TransInterFormContext";
import { useValidateSwiftMutation } from "@/services/internationalTransferAPI";
import i18n from "i18n/i18n";

// Ký tự hợp lệ cho nội dung/thông báo — hiển thị dưới mỗi textarea.
const getAllowedCharsNote = () => (i18n.t("ALLOWED_CHARACTERS_NOTE"));

const TransInterStepTwo: React.FC<TransInterStepTwoProps> = ({
  onSubmit,
  onBackStep,
}) => {
  const { t } = useTranslation();
  const form = useTransFormContext();
  const [saveOpened, { open: openSave, close: closeSave }] =
    useDisclosure(false);
  const [saveNote, setSaveNote] = useState("");

  const [validateSwift] = useValidateSwiftMutation();

  // Validate định dạng SWIFT/BIC realtime (onBlur) — set lỗi vào form nếu sai (E08).
  const handleSwiftBlur = async (
    field: "beneficiarySwiftCode" | "intermediaryBankSwift",
    value?: string,
  ) => {
    const code = (value || "").trim();
    if (!code) {
      form.clearFieldError(field);
      return;
    }
    try {
      const res = await validateSwift({ swiftCode: code }).unwrap();
      if (res?.body?.valid) {
        form.clearFieldError(field);
      } else {
        form.setFieldError(field, t("TPL_FIELD_INVALID", { p0: t("SWIFT_CODE") }));
      }
    } catch {
      // Bỏ qua lỗi mạng khi validate realtime
    }
  };

  // Nhập số tài khoản bất kỳ → tự điền các trường người thụ hưởng (mock, chưa nối core).
  const handleAutoFillBeneficiary = (accountNo?: string) => {
    const acc = (accountNo || "").trim();
    if (!acc) return;
    form.setFieldValue("beneficiaryName", "NGUYEN VAN A");
    form.setFieldValue("beneficiaryAddress", "123 Example Street, London");
    form.setFieldValue("beneficiarySwiftCode", "BFTVVNVX");
    form.setFieldValue("beneficiaryBankName", "Example Bank Ltd");
    form.setFieldValue("beneficiaryBankAddress", "London, United Kingdom");
    form.clearFieldError("beneficiaryName");
    form.clearFieldError("beneficiarySwiftCode");
    form.clearFieldError("beneficiaryBankName");
  };

  // Xóa ngân hàng trung gian (không bắt buộc).
  const handleClearIntermediary = () => {
    form.setFieldValue("intermediaryBankSwift", "");
    form.setFieldValue("intermediaryBankName", "");
    form.setFieldValue("intermediaryBankAddress", "");
  };

  const handleSaveTransaction = () => {
    // Chưa ghép API — chỉ đóng modal (lưu nháp giao dịch sẽ nối sau).
    closeSave();
    setSaveNote("");
  };

  return (
    <>
      <Flex gap={20} align="flex-start">
        {/* CỘT TRÁI */}
        <Box className={classes.sourceAccount} w="62%">
          <Stack spacing={20}>
            <Text fw={600} fz={16} c="#212936">
              {t("BENEFICIARY_INFORMATION") || t("BENEFICIARY_INFORMATION")}
            </Text>
            <Flex justify="space-between" gap={16}>
              <TextInput
                label={t("ACCOUNT_NUMBER")}
                withAsterisk
                radius={8}
                w="50%"
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={35}
                {...form.getInputProps("beneficiaryAccount")}
                onChange={(e) => {
                  // Mặc định chỉ cho nhập số; nếu SWIFT là Ai Cập (EG)/UAE (AE)
                  // thì cho nhập IBAN có chữ. Quốc gia suy từ ký tự 5-6 của SWIFT.
                  const swift = (
                    form.values.beneficiarySwiftCode || ""
                  ).toUpperCase();
                  const country =
                    swift.length >= 6 ? swift.substring(4, 6) : "";
                  const allowLetters = country === "EG" || country === "AE";
                  const raw = e.currentTarget.value.toUpperCase();
                  const cleaned = (
                    allowLetters
                      ? raw.replace(/[^A-Z0-9]/g, "")
                      : raw.replace(/[^0-9]/g, "")
                  ).slice(0, 35);
                  form.setFieldValue("beneficiaryAccount", cleaned);
                }}
                onBlur={() =>
                  handleAutoFillBeneficiary(form.values.beneficiaryAccount)
                }
              />
              <TextInput
                label={t("ACCOUNT_NICKNAME")}
                withAsterisk
                radius={8}
                w="50%"
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={35}
                {...form.getInputProps("beneficiaryName")}
              />
            </Flex>
            <Textarea
              label={t("ADDRESS")}
              radius={8}
              minRows={2}
              w="100%"
              placeholder={t("ENTER_PLACEHOLDER")}
              maxLength={200}
              {...form.getInputProps("beneficiaryAddress")}
            />

            <Text fw={600} fz={16} c="#212936">
              {t("BENEFICIARY_BANK") || t("BENEFICIARY_BANK")}
            </Text>
            <Flex justify="space-between" gap={16}>
              <TextInput
                label={`${t("SWIFT_CODE")}`}
                withAsterisk
                radius={8}
                w={139}
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={11}
                {...form.getInputProps("beneficiarySwiftCode")}
                onChange={(e) =>
                  form.setFieldValue(
                    "beneficiarySwiftCode",
                    e.currentTarget.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 11),
                  )
                }
                onBlur={() =>
                  handleSwiftBlur(
                    "beneficiarySwiftCode",
                    form.values.beneficiarySwiftCode,
                  )
                }
              />
              <TextInput
                label={`${t("BANK_NAME")}`}
                withAsterisk
                radius={8}
                w={230}
                placeholder={t("ENTER_PLACEHOLDER")}
                {...form.getInputProps("beneficiaryBankName")}
              />
              <TextInput
                label={`${t("ADDRESS")}`}
                radius={8}
                style={{ flex: 1 }}
                placeholder={t("ENTER_PLACEHOLDER")}
                {...form.getInputProps("beneficiaryBankAddress")}
              />
            </Flex>

            <Box>
              <Flex justify="space-between" mb={4}>
                <Text fz={14} c="#212936">
                  {t("DETAILED_CONTENT") || t("DETAILED_CONTENT")}
                </Text>
                <Text fz={13} c="#6B7280">
                  {(form.values.detailContent || "").length}{t("OF_200_CHARACTERS")}
                </Text>
              </Flex>
              <Textarea
                radius={8}
                minRows={4}
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={200}
                value={form.values.detailContent || ""}
                error={form.errors.detailContent}
                onChange={(e) =>
                  form.setFieldValue(
                    "detailContent",
                    e.currentTarget.value.replace(/[^A-Za-z0-9 .,+/-]/g, ""),
                  )
                }
              />
              <Text fz={12} c="#6B7280" mt={4}>
                {getAllowedCharsNote()}
              </Text>
            </Box>

            <Box>
              <Flex justify="space-between" mb={4}>
                <Text fz={14} c="#212936">
                  {t("NOTICE_TO_THE_BANK") || t("NOTICE_TO_THE_BANK")}
                </Text>
                <Text fz={13} c="#6B7280">
                  {(form.values.bankNotice || "").length}{t("OF_200_CHARACTERS")}
                </Text>
              </Flex>
              <Textarea
                radius={8}
                minRows={4}
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={200}
                value={form.values.bankNotice || ""}
                error={form.errors.bankNotice}
                onChange={(e) =>
                  form.setFieldValue(
                    "bankNotice",
                    e.currentTarget.value.replace(/[^A-Za-z0-9 .,+/-]/g, ""),
                  )
                }
              />
              <Text fz={12} c="#6B7280" mt={4}>
                {getAllowedCharsNote()}
              </Text>
            </Box>
          </Stack>
        </Box>

        {/* CỘT PHẢI — ngân hàng trung gian (không bắt buộc, xóa được) */}
        <Box style={{ flex: 1 }} className={classes.sourceAccount}>
          <Stack spacing={12}>
            <Flex justify="space-between" align="center">
              <Text fz={20} fw={600} c="#212936">
                {t("INTERMEDIARY_BANK") || t("INTERMEDIARY_BANK")}
              </Text>
              <ActionIcon
                variant="light"
                color="gray"
                radius={8}
                onClick={handleClearIntermediary}
                title={t("DELETE_INTERMEDIARY_BANK")}
              >
                <IconTrash size={18} color="#6B7280" />
              </ActionIcon>
            </Flex>
            <Flex gap={16}>
              <TextInput
                radius={8}
                label={`${t("SWIFT_CODE")}`}
                w={190}
                placeholder={t("ENTER_PLACEHOLDER")}
                maxLength={11}
                {...form.getInputProps("intermediaryBankSwift")}
                onChange={(e) =>
                  form.setFieldValue(
                    "intermediaryBankSwift",
                    e.currentTarget.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 11),
                  )
                }
                onBlur={() =>
                  handleSwiftBlur(
                    "intermediaryBankSwift",
                    form.values.intermediaryBankSwift,
                  )
                }
              />
              <TextInput
                radius={8}
                label={`${t("BANK_NAME")}`}
                style={{ flex: 1 }}
                placeholder={t("ENTER_PLACEHOLDER")}
                {...form.getInputProps("intermediaryBankName")}
              />
            </Flex>
            <Textarea
              radius={8}
              minRows={2}
              label={`${t("ADDRESS")}`}
              w="100%"
              placeholder={t("ENTER_PLACEHOLDER")}
              {...form.getInputProps("intermediaryBankAddress")}
            />
          </Stack>
        </Box>
      </Flex>

      <Space h={20} />

      {/* Footer cố định */}
      <Box
        style={{
          position: "fixed",
          bottom: 0,
          left: "calc(var(--mantine-navbar-width, 0px) + 20px)",
          right: 0,
          padding: "10px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0px 5px 15px 0px #00000026",
          borderRadius: "12px",
          height: "76px",
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <Group
          spacing={6}
          onClick={openSave}
          style={{ cursor: "pointer" }}
          c="#2A81D0"
        >
          <IconDeviceFloppy size={18} />
          <Text fz={14} fw={600}>
            {t("SAVE_TRANSACTION")}
          </Text>
        </Group>
        <Flex gap={10}>
          <Button
            onClick={onBackStep}
            sx={() => ({
              width: "100px",
              backgroundColor: "#F4F4F6",
              color: "#525866",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
              "&:hover": { backgroundColor: "#F4F4F6" },
            })}
          >
            {t("BACK") || t("GO_BACK")}
          </Button>
          <Button
            type="submit"
            onClick={() => {
              const fields = [
                "beneficiaryAccount",
                "beneficiaryName",
                "beneficiaryAddress",
                "beneficiarySwiftCode",
                "beneficiaryBankName",
                "detailContent",
                "bankNotice",
              ] as const;
              const hasError = fields
                .map((f) => form.validateField(f).hasError)
                .some(Boolean);
              if (!hasError) onSubmit?.();
            }}
            sx={() => ({
              width: "160px",
              backgroundColor: "#2A81D0",
              color: "white",
              borderRadius: "8px",
              padding: "8px 10px",
              fontWeight: 400,
            })}
          >
            {t("NEXT") || t("NEXT")}
          </Button>
        </Flex>
      </Box>

      {/* Modal lưu giao dịch */}
      <Modal
        opened={saveOpened}
        onClose={closeSave}
        centered
        size={420}
        withCloseButton={false}
      >
        <Stack spacing="md" p="md">
          <Text fw={700} fz="lg" ta="center" style={{ whiteSpace: "nowrap" }}>
            {t("SAVE_THIS_TRANS")}
          </Text>
          <Box>
            <Text fz={14} c="#212936" mb={6}>
              {t("NOTE_OPTIONAL")}
            </Text>
            <TextInput
              radius={8}
              placeholder="Enter"
              value={saveNote}
              onChange={(e) => setSaveNote(e.currentTarget.value)}
            />
          </Box>
          <Button
            fullWidth
            onClick={handleSaveTransaction}
            sx={() => ({
              backgroundColor: "#2A81D0",
              borderRadius: 8,
              "&:hover": { backgroundColor: "#2470bb" },
            })}
          >
            {t("SAVE")}
          </Button>
          <Text
            ta="center"
            fz={14}
            fw={600}
            c="#2A81D0"
            style={{ cursor: "pointer" }}
            onClick={closeSave}
          >
            {t("CANCEL")}
          </Text>
        </Stack>
      </Modal>

      <Space h={rem(20)} />
    </>
  );
};

export default TransInterStepTwo;
